<template>
  <div class="contact w-full">
    <!-- Video Background Section -->
    <section class="relative w-full h-[60vh] md:h-[70vh] lg:h-[80vh] overflow-hidden" style="margin-top: 80px;">
      <div class="video-bg">
        <iframe 
          ref="contactVideo"
          src="https://player.vimeo.com/video/1104927366?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1&controls=0&portrait=0&sidedock=0&dnt=1&transparent=0"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture"
        ></iframe>
      </div>
      
      <!-- Overlay -->
      <div class="absolute inset-0 bg-black bg-opacity-20 z-10"></div>
      
      <!-- Phone Section - Sobre o vídeo -->
      <div class="absolute inset-0 z-20 flex items-center justify-center px-4">
        <div class="text-center">
          <p class="text-hanken text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light leading-tight uppercase tracking-wider text-white">
            <a 
              :href="`tel:${$t('contact.phone.number').replace(/\s/g, '')}`"
              class="block sm:inline typewriter-text phone-link"
              ref="typewriterText"
            >
              {{ typewriterText }}
            </a>
          </p>
        </div>
      </div>
    </section>

    <!-- Contact Form Section -->
    <section class="bg-white py-20 px-6">
      <div class="max-w-2xl mx-auto">
        <!-- Or Send Message Text -->
        <div class="mb-16 text-center">
          <h2 class="text-2xl font-bold text-gray-900 uppercase tracking-wider">
            {{ $t('contact.form.orSendMessage') }}
          </h2>
        </div>

        <!-- Formulário -->
        <form v-if="!showSuccess && !showError" @submit.prevent="handleFormSubmit">
          <!-- Passo 1: Campos do Formulário -->
          <div v-if="!showTurnstile" class="space-y-6">
            <!-- Nome e Email na mesma linha -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div class="border border-black p-2" :class="{ 'border-red-500': errors.name }">
                  <input
                    v-model="form.name"
                    @blur="validateField('name')"
                    @input="clearError('name')"
                    type="text"
                    id="name"
                    class="w-full bg-transparent border-none outline-none text-lg font-light text-gray-900 placeholder-gray-900 placeholder:text-center placeholder:font-bold text-left focus:ring-0"
                    :placeholder="$t('contact.form.name')"
                  />
                </div>
                <span v-if="errors.name" class="text-red-500 text-sm mt-2 block">{{ errors.name }}</span>
              </div>
              
              <div>
                <div class="border border-black p-2" :class="{ 'border-red-500': errors.email }">
                  <input
                    v-model="form.email"
                    @blur="validateField('email')"
                    @input="clearError('email')"
                    type="email"
                    id="email"
                    class="w-full bg-transparent border-none outline-none text-lg font-light text-gray-900 placeholder-gray-900 placeholder:text-center placeholder:font-bold text-left focus:ring-0"
                    :placeholder="$t('contact.form.email')"
                  />
                </div>
                <span v-if="errors.email" class="text-red-500 text-sm mt-2 block">{{ errors.email }}</span>
              </div>
            </div>
            
            <!-- Assunto -->
            <div>
              <div class="border border-black p-2" :class="{ 'border-red-500': errors.subject }">
                <input
                  v-model="form.subject"
                  @blur="validateField('subject')"
                  @input="clearError('subject')"
                  type="text"
                  id="subject"
                  class="w-full bg-transparent border-none outline-none text-lg font-light text-gray-900 placeholder-gray-900 placeholder:text-center placeholder:font-bold text-left focus:ring-0"
                  :placeholder="$t('contact.form.subject')"
                />
              </div>
              <span v-if="errors.subject" class="text-red-500 text-sm mt-2 block">{{ errors.subject }}</span>
            </div>
            
            <!-- Mensagem -->
            <div>
              <div class="border border-black p-2" :class="{ 'border-red-500': errors.message }">
                <textarea
                  v-model="form.message"
                  @blur="validateField('message')"
                  @input="clearError('message')"
                  id="message"
                  rows="4"
                  class="w-full bg-transparent border-none outline-none text-lg font-light text-gray-900 placeholder-gray-900 placeholder:text-center placeholder:font-bold text-left resize-none focus:ring-0"
                  :placeholder="$t('contact.form.message')"
                ></textarea>
              </div>
              <span v-if="errors.message" class="text-red-500 text-sm mt-2 block">{{ errors.message }}</span>
            </div>
            
            <!-- Submit Button - Passo 1 -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="!isFormValid"
                class="w-full bg-black text-white py-4 px-8 text-sm font-light uppercase tracking-wide hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ $t('contact.form.submit') }}
              </button>
            </div>
          </div>

          <!-- Passo 2: Apenas Turnstile CAPTCHA -->
          <div v-if="showTurnstile" class="space-y-6">
            <div class="pt-2">
              <div 
                ref="turnstileWidget"
                class="cf-turnstile-container flex justify-center"
              ></div>
              <span v-if="errors.turnstile" class="text-red-500 text-sm mt-1 block text-center">{{ errors.turnstile }}</span>
            </div>
            
            <!-- Submit Button - Passo 2 -->
            <div class="pt-2">
              <button
                type="button"
                @click="submitForm"
                :disabled="isSubmitting || !turnstileToken"
                class="w-full bg-black text-white py-4 px-8 text-sm font-light uppercase tracking-wide hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isSubmitting">{{ $t('contact.form.sending') }}</span>
                <span v-else>{{ $t('contact.form.submit') }}</span>
              </button>
            </div>
          </div>
        </form>

        <!-- Mensagem de Erro -->
        <div v-if="showError" class="w-full max-w-4xl mx-auto text-center">
          <div class="bg-white border-2 border-black p-8">
            <div class="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100">
              <svg class="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h3 class="text-xl font-light text-gray-900 mb-2">
              {{ $t('contact.messages.error.title') }}
            </h3>
            <p class="text-gray-600 mb-6">
              {{ $t('contact.messages.error.subtitle') }}
            </p>
            <button 
              @click="resetToForm"
              class="inline-flex items-center px-6 py-3 text-sm font-light bg-black text-white hover:bg-gray-800 transition-colors"
            >
              {{ $t('contact.form.tryAgain') }}
            </button>
          </div>
        </div>

        <!-- Mensagem de Sucesso -->
        <div v-if="showSuccess" class="w-full max-w-4xl mx-auto text-center">
          <div class="bg-white border-2 border-black p-8">
            <div class="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100">
              <svg class="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 class="text-xl font-light text-gray-900 mb-2">
              {{ $t('contact.messages.success.title') }}
            </h3>
            <p class="text-gray-600 mb-6">
              {{ $t('contact.messages.success.subtitle') }}
            </p>
            <button 
              @click="resetToForm"
              class="inline-flex items-center px-6 py-3 text-sm font-light bg-black text-white hover:bg-gray-800 transition-colors"
            >
              {{ $t('contact.form.sendAnother') }}
            </button>
          </div>
        </div>

      </div>
    </section>
  </div>
</template>

<script>
import { gsap } from 'gsap'

// Constants
const VALIDATION_RULES = {
  name: {
    minLength: 2,
    maxLength: 50,
    required: true
  },
  email: {
    required: true
  },
  subject: {
    minLength: 5,
    maxLength: 100,
    required: true
  },
  message: {
    minLength: 10,
    maxLength: 1000,
    required: true
  }
}

// ERROR_MESSAGES will be replaced with translations
const ERROR_MESSAGES = {}

export default {
  name: 'Contact',
  mounted() {
    this.setupVideoLoading()
    this.setupTypewriterAnimation()
    // Turnstile será inicializado apenas quando necessário (Passo 2)
  },
  data() {
    return {
      form: {
        name: '',
        email: '',
        subject: '',
        message: ''
      },
      errors: {},
      isSubmitting: false,
      showSuccess: false,
      showError: false,
      showTurnstile: false,
      turnstileToken: null,
      turnstileWidgetId: null,
      typewriterText: '',
      typewriterIndex: 0,
      isTypewriterComplete: false
    }
  },
  computed: {
    isFormValid() {
      // Validação apenas dos campos (sem Turnstile)
      const isValid = this.form.name.trim() !== '' &&
                     this.form.email.trim() !== '' &&
                     this.form.subject.trim() !== '' &&
                     this.form.message.trim() !== '' &&
                     this.isValidEmail(this.form.email) &&
                     Object.keys(this.errors).length === 0
      
      return isValid
    }
  },
  watch: {
    // Reagir às mudanças de idioma
    '$i18n.locale'() {
      this.setupTypewriterAnimation()
    }
  },
  methods: {
    /**
     * Handle form submit - Passo 1: Validar campos e mostrar Turnstile
     */
    handleFormSubmit() {
      if (!this.validateForm()) {
        return
      }
      
      // Mostrar Turnstile (Passo 2)
      this.showTurnstile = true
      
      // Aguardar um pouco para o DOM atualizar e então inicializar Turnstile
      this.$nextTick(() => {
        this.initializeTurnstile()
      })
    },

    /**
     * Validate a specific form field
     * @param {string} fieldName - Name of the field to validate
     */
    validateField(fieldName) {
      const value = this.form[fieldName].trim()
      const rules = VALIDATION_RULES[fieldName]
      
      // Check required field
      if (rules.required && !value) {
        this.errors[fieldName] = this.$t(`contact.validation.${fieldName}.required`)
        return
      }
      
      // Check minimum length
      if (rules.minLength && value.length < rules.minLength) {
        this.errors[fieldName] = this.$t(`contact.validation.${fieldName}.minLength`)
        return
      }
      
      // Check maximum length
      if (rules.maxLength && value.length > rules.maxLength) {
        this.errors[fieldName] = this.$t(`contact.validation.${fieldName}.maxLength`)
        return
      }
      
      // Check email format
      if (fieldName === 'email' && value && !this.isValidEmail(value)) {
        this.errors[fieldName] = this.$t(`contact.validation.${fieldName}.invalid`)
        return
      }
      
      // Clear error if validation passes
      delete this.errors[fieldName]
    },
    
    /**
     * Clear error for a specific field
     * @param {string} fieldName - Name of the field
     */
    clearError(fieldName) {
      if (this.errors[fieldName]) {
        delete this.errors[fieldName]
      }
      this.showSuccess = false
      this.showError = false
    },
    
    /**
     * Validate email format
     * @param {string} email - Email to validate
     * @returns {boolean} - True if valid
     */
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    
    /**
     * Validate entire form
     * @returns {boolean} - True if all validations pass
     */
    validateForm() {
      Object.keys(VALIDATION_RULES).forEach(field => {
        this.validateField(field)
      })
      
      return Object.keys(this.errors).length === 0
    },
    
    /**
     * Reset form to initial state
     */
    resetForm() {
      this.form = {
        name: '',
        email: '',
        subject: '',
        message: ''
      }
      this.errors = {}
      this.turnstileToken = null
      
      // Reset Turnstile widget
      if (window.turnstile && this.turnstileWidgetId) {
        window.turnstile.reset(this.turnstileWidgetId)
      }
    },
    
    /**
     * Show success message (permanente até user clicar)
     */
    showSuccessMessage() {
      this.showSuccess = true
      // Não auto-hide mais, user decide quando voltar
    },

    /**
     * Reset to form (voltar ao formulário)
     */
    resetToForm() {
      this.showSuccess = false
      this.showError = false
      this.showTurnstile = false
      // Não limpar os campos - manter dados preenchidos
      this.errors = {}
      this.turnstileToken = null
      
      // Reset Turnstile widget se existir
      if (window.turnstile && this.turnstileWidgetId) {
        try {
          window.turnstile.remove(this.turnstileWidgetId)
        } catch (error) {
          // Widget já removido ou não existe
        }
        this.turnstileWidgetId = null
      }
    },
    
    /**
     * Show error message (permanente até user clicar)
     */
    showErrorMessage() {
      this.showError = true
      // Não auto-hide mais, user decide quando voltar
    },
    
    /**
     * Submit form data
     */
    async submitForm() {
      if (!this.validateForm()) {
        return
      }

      // Validar Turnstile
      if (!this.turnstileToken) {
        this.errors.turnstile = this.$t('contact.validation.turnstile.required')
        return
      }
      
      this.isSubmitting = true
      
      try {
        // Dados do formulário incluindo token Turnstile
        const formData = {
          ...this.form,
          turnstileToken: this.turnstileToken
        }
        
        
        // Enviar para o backend PHP
        const response = await fetch('/save.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        })
        
        const result = await response.json()
        
        if (!response.ok) {
          throw new Error(result.error || 'Erro ao enviar mensagem')
        }
        
        this.showSuccessMessage()
        this.resetForm()
        
      } catch (error) {
        this.showErrorMessage()
        
        // Reset Turnstile em caso de erro
        if (window.turnstile && this.turnstileWidgetId) {
          window.turnstile.reset(this.turnstileWidgetId)
          this.turnstileToken = null
        }
      } finally {
        this.isSubmitting = false
      }
    },

    /**
     * Setup typewriter animation
     */
    setupTypewriterAnimation() {
      // Aguardar o DOM estar pronto
      this.$nextTick(() => {
        // Usar o texto completo traduzido
        const fullText = `${this.$t('contact.phone.title')} ${this.$t('contact.phone.number')}`
        this.typewriterText = ''
        this.typewriterIndex = 0
        this.isTypewriterComplete = false
        
        // Configurar o texto inicial como vazio
        if (this.$refs.typewriterText) {
          this.$refs.typewriterText.textContent = ''
        }
        
        // Iniciar animação após um pequeno delay
        setTimeout(() => {
          this.startTypewriterAnimation(fullText)
        }, 3000) // Delay de 3 segundos para começar
      })
    },

    /**
     * Start typewriter animation
     */
    startTypewriterAnimation(fullText) {
      const speed = 100 // Velocidade em ms por caractere
      const pauseAtColon = 800 // Pausa extra no ":" 
      
      const typeNextChar = () => {
        if (this.typewriterIndex < fullText.length) {
          const char = fullText[this.typewriterIndex]
          
          // Adicionar caractere
          this.typewriterText += char
          this.typewriterIndex++
          
          // Pausa especial no ":"
          if (char === ':') {
            setTimeout(typeNextChar, pauseAtColon)
          } else {
            setTimeout(typeNextChar, speed)
          }
        } else {
          // Animação completa
          this.isTypewriterComplete = true
          this.addCursorBlink()
        }
      }
      
      typeNextChar()
    },

    /**
     * Add blinking cursor effect
     */
    addCursorBlink() {
      if (this.$refs.typewriterText) {
        // Adicionar cursor piscante
        const cursor = document.createElement('span')
        cursor.textContent = '|'
        cursor.className = 'cursor-blink'
        cursor.style.opacity = '1'
        this.$refs.typewriterText.appendChild(cursor)
        
        // Animar cursor piscante
        gsap.to(cursor, {
          opacity: 0,
          duration: 0.8,
          repeat: -1,
          yoyo: true,
          ease: "power2.inOut"
        })
      }
    },

    /**
     * Setup video loading detection
     */
    setupVideoLoading() {
      const iframe = this.$refs.contactVideo
      if (iframe) {
        iframe.addEventListener('load', () => {
          // Emitir evento para o App.vue
          window.dispatchEvent(new CustomEvent('video-loaded', { detail: 'contact' }))
        })
        
        // Fallback: se o iframe já estiver carregado
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
          window.dispatchEvent(new CustomEvent('video-loaded', { detail: 'contact' }))
        }
      }
    },


    /**
     * Recreate Turnstile widget (para resetToForm)
     */
    recreateTurnstile() {
      
      // Limpar widget existente
      if (window.turnstile && this.turnstileWidgetId) {
        try {
          window.turnstile.remove(this.turnstileWidgetId)
        } catch (error) {
          // Widget já removido ou não existe
        }
      }
      
      // Resetar state
      this.turnstileWidgetId = null
      this.turnstileToken = null
      
      // Aguardar DOM atualizar e recriar widget
      this.$nextTick(() => {
        if (this.$refs.turnstileWidget && window.turnstile) {
          // Limpar conteúdo do container
          this.$refs.turnstileWidget.innerHTML = ''
          
          // Pequeno delay para garantir limpeza
          setTimeout(() => {
            this.initializeTurnstile()
          }, 100)
        }
      })
    },



    /**
     * Initialize Turnstile widget (Modo Gerenciado) - Callbacks Inline
     */
    initializeTurnstile() {
      
      // Aguardar o script do Turnstile carregar
      const checkTurnstile = () => {
        if (window.turnstile && this.$refs.turnstileWidget) {
          try {
            this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileWidget, {
              sitekey: '0x4AAAAAABoqPZfntOuaJoyV',
              callback: (token) => {
                this.turnstileToken = token
                if (this.errors.turnstile) {
                  delete this.errors.turnstile
                }
                // Forçar reatividade
                this.$forceUpdate()
              },
              'error-callback': (error) => {
                this.turnstileToken = null
                this.errors.turnstile = 'Erro na verificação de segurança. Tente novamente.'
                this.$forceUpdate()
              },
              'expired-callback': () => {
                this.turnstileToken = null
                this.errors.turnstile = 'Verificação de segurança expirada. Refaça a verificação.'
                this.$forceUpdate()
              },
              theme: 'light'
            })
          } catch (error) {
            // Erro ao renderizar Turnstile
          }
        } else {
          setTimeout(checkTurnstile, 100)
        }
      }
      checkTurnstile()
    }
  }
}
</script>

<style scoped>
/* Autofill styles removal */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active,
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus,
textarea:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px white inset !important;
  -webkit-text-fill-color: #1f2937 !important;
}

/* Focus styles */
input:focus,
textarea:focus {
  outline: none !important;
  box-shadow: none !important;
}

/* Error state styles */
.border-red-500 {
  border-color: #ef4444 !important;
}

/* Turnstile widget styles */
.cf-turnstile-container {
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 65px;
}

/* Video background styles */
.video-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.video-bg iframe {
  width: 100vw;
  height: 56.25vw; /* 16:9 aspect ratio */
  min-height: 100%;
  min-width: 177.77vh; /* 16:9 aspect ratio */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Typewriter animation styles */

.phone-link {
  color: inherit;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  display: inline-block;
}

.phone-link:hover {
  opacity: 0.8;
  transform: scale(1.02);
  text-decoration: none;
}

.phone-link:active {
  transform: scale(0.98);
}

.cursor-blink {
  color: #ffffff;
  font-weight: normal;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

</style> 